<script lang="ts">
import {computed, defineComponent, Ref, ref, watch} from '@vue/composition-api'

import leviathanApi from '@/api/leviathan.api'
import MapitDialog from '@/components/dialog/MapitDialog.vue'
import AvailableDeviceLookup from '@/components/general/AvailableDeviceLookup.vue'
import CardWrapper from '@/components/general/CardWrapper.vue'
import {MapitDevice, Product} from '@/types/dynamodb-types'
import {mapGetters} from '@/utils/map-store'

export default defineComponent({
  name: 'RegistrationMapitConnect',
  components: {MapitDialog, AvailableDeviceLookup, CardWrapper},
  setup(props, {root}) {
    const imei = ref('')
    const device = ref(null) as Ref<MapitDevice | null>
    const loading = ref(false)
    const dialog = ref(false)
    const dialogLoading = ref(false)
    const error = ref(null) as Ref<string | null>

    const {getGroup: group} = mapGetters(root.$store.getters)

    const buttonStyles = computed(() => {
      return `
        text-transform: uppercase !important;
        padding: 8px 24px !important;
        font-family: 'ProximaNovaExCnSbold', sans-serif !important;
        letter-spacing: 0.01em !important;
     `
    })

    const isValidImei = computed(() => {
      return imei.value.length === 15
    })

    watch(isValidImei, valid => {
      if (!valid) {
        device.value = null
      }
    })

    async function handleRegisterSale() {
      if (!device.value) return
      loading.value = true
      try {
        await leviathanApi.sellDevice(group.value.dealerId, device.value.id, [Product.MapitConnect])
      } catch (e: any) {
        error.value = e.message
      } finally {
        loading.value = false
        dialog.value = true
      }
    }

    async function handleCloseModal() {
      if (error.value) {
        dialog.value = false
        error.value = null
      } else {
        dialogLoading.value = true
        await root.$router.push('/registrations/mapit-only')
      }
    }

    return {
      imei,
      device,
      loading,
      dialog,
      buttonStyles,
      isValidImei,
      error,
      dialogLoading,
      handleRegisterSale,
      handleCloseModal,
    }
  },
})
</script>

<template>
  <div>
    <card-wrapper
      id="registration-mapit-connect"
      :title="$t('honda.registration.connect.title')"
      :hide-close-btn="false"
      @openCloseCancelDialog="$router.push('/registrations/mapit-only')">
      <div class="mapit-connect-wrapper">
        <div class="mapit-connect-logo">
          <img class="mapit-logo" src="/images/registrations/logo.svg" alt="Logo" />
          <img class="mapit-text" src="/images/registrations/logo-text.svg" alt="Mapit" />
        </div>
        <span class="mapit-connect-subtitle">
          {{ $t('honda.registration.connect.subtitle') }}
        </span>
        <div class="mapit-connect-inputs">
          <div class="mapit-input-item">
            <p>{{ $t('honda.registration.connect.imei') }}</p>
            <available-device-lookup id="device-input" v-model="device" @imei-change="val => (imei = val)" />
          </div>
        </div>
        <m-btn
          color="primary"
          class="button-padding action-button"
          @click="handleRegisterSale()"
          :style="buttonStyles"
          :loading="loading"
          :disabled="loading || !device || !isValidImei">
          {{ $t('honda.registration.connect.action') }}
        </m-btn>
      </div>
      <mapit-dialog :dialog="dialog" @close="handleCloseModal()" customId="popup-confirm-lost-lead">
        <template v-slot:header-icon>
          <v-icon v-if="!error" class="text-secondary">fa-solid fa-check</v-icon>
          <v-icon v-else class="text-primary">fa-solid fa-circle-xmark</v-icon>
        </template>
        <template v-slot:header-text>
          <div class="text-center" v-if="!error">{{ $t('honda.registration.connect.success.title') }}</div>
          <div class="text-center" v-else>{{ $t('honda.registration.connect.error.title') }}</div>
        </template>
        <template v-slot:body>
          <div class="text-center">
            <div v-if="!error" v-balance-text.children v-html="$t('honda.registration.connect.success.description')" />
            <div v-else v-balance-text.children v-html="$t('honda.registration.connect.error.description')" />
          </div>
        </template>
        <template v-slot:footer-center>
          <v-btn class="btn-primary" @click="handleCloseModal()" :loading="dialogLoading" :disabled="dialogLoading">
            {{ $t('buttons.confirm') }}
          </v-btn>
        </template>
      </mapit-dialog>
    </card-wrapper>
  </div>
</template>

<style>
#device-input .m-text-field input,
#device-input .m-text-field .v-messages__message {
  text-align: center !important;
}
</style>
<style lang="scss" scoped>
.mapit-connect-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 0 30px 0;

  .mapit-connect-logo {
    position: relative;
    width: 100%;
    height: 6rem;
    margin-bottom: 3rem;

    .mapit-text,
    .mapit-logo {
      width: 15rem;
      left: 50%;
      position: absolute;
      transform: translateX(-50%);
    }

    .mapit-logo {
      z-index: 1;
      background: linear-gradient(90deg, white 0%, transparent 40%);
    }

    .mapit-text {
      z-index: 0;
      animation-name: slide-in-left;
      animation-duration: 1s;
      animation-timing-function: cubic-bezier(0.68, -0.55, 0.265, 1.55);
    }
  }

  .mapit-connect-subtitle {
    font-family: 'ProximaNovaExCnLight', sans-serif;
    font-weight: 300;
    font-size: 24px;
    margin-bottom: 1.5rem;
  }

  .mapit-connect-inputs {
    max-width: 512px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 3rem;
    background: hsla(0, 0%, 12%, 0.06);
    border-radius: 4px;
    padding: 1.5rem 0 2rem 0;

    .mapit-input-item {
      width: 230px;

      p {
        margin-bottom: 0.2rem;
      }
    }
  }
}

@keyframes slide-in-left {
  0% {
    opacity: 0;
    transform: translateX(-80%);
  }
  100% {
    opacity: 1;
    transform: translateX(-50%);
  }
}
</style>
